import { t } from 'i18next';
import * as Yup from 'yup';
import { Box, Tooltip, Typography } from '@mui/material';
import {
  AREA_TRANSACTION_FILTER_FIELDS,
  AREA_TRANSACTION_TYPES,
  CLIENT_TRANSACTION_FILTER_FIELDS,
  CLIENT_TRANSACTION_TYPES,
} from '@globalConstants';
import i18n from '@i18n';
import { parseAmount, formatDate } from '@utils';
import { VALIDATE_DATE, VALIDATE_STRING } from '@validations';
import styles from './styles';

export const TRANSACTION_DEBITS = ['CASHIN_PPCAAS', 'CASHIN_TJURIDICA'];

export const DEBIT_TRANSACTIONS = ['DEBIT', 'DISPERSION', 'REASSIGNMENT'];

const RECOVERY_TYPES = {
  INDIVIDUAL: 'INDIVIDUAL',
  AUTOMATIC: 'AUTOMATIC',
  SELECTIVE: 'SELECTIVE',
};

const CUSTOMER_TRANSACTIONS_TYPES = {
  ADJUSTMENT: 'ADJUSTMENT',
  BALANCE_RECOVERY: 'BALANCE_RECOVERY',
  DISPERSION: 'DISPERSION',
  FUND: 'FUND',
  ASSIGNMENT: 'ASSIGNMENT',
  REASSIGNMENT: 'REASSIGNMENT',
};

/**
 * @param {string} transactionType
 * @param {string} movementType
 * @param {string} accountNumber
 * @param {string} recoveryType
 * @param {string} areaName
 * @returns Receives the transaction type and returns the corresponding translation. If no translation available, returns the id.
 */
function getConcept(transactionType, movementType, accountNumber, recoveryType, areaName) {
  let key = transactionType ?? movementType;

  if (transactionType === CUSTOMER_TRANSACTIONS_TYPES.ADJUSTMENT) {
    key = `ADJUSTMENT_${movementType}`;
  }

  let translation = CLIENT_TRANSACTION_TYPES?.[key] || AREA_TRANSACTION_TYPES?.[key];

  if (
    transactionType === CUSTOMER_TRANSACTIONS_TYPES.BALANCE_RECOVERY &&
    recoveryType === RECOVERY_TYPES.INDIVIDUAL
  ) {
    translation += '_ACCOUNT';
  }

  if (
    (transactionType === CUSTOMER_TRANSACTIONS_TYPES.ASSIGNMENT ||
      transactionType === CUSTOMER_TRANSACTIONS_TYPES.REASSIGNMENT) &&
    areaName
  ) {
    translation += '_TO_AREA';
  }

  if (translation) {
    const text = t(translation, { accountNumber, areaName });
    const isLongText = text.length > 25;
    return (
      <Tooltip title={isLongText && text} arrow>
        <Box style={styles.rowEllipsis}>{text}</Box>
      </Tooltip>
    );
  }

  return transactionType;
}

export const COLUMNS = [
  { title: 'COMMON:DATE', content: (item) => formatDate(item.createdAt) },
  {
    title: 'COMMON:CONCEPT',
    content: (item) =>
      getConcept(
        item.customerTransactionType,
        item.movementType,
        item.sourceAccountNumber,
        item.recoveryType,
        item.areaName
      ),
  },
  {
    title: 'COMMON:AMOUNT',
    content: (item) => {
      let sign = '+';
      let color = 'primary.light';

      if (DEBIT_TRANSACTIONS.includes(item.movementType)) {
        sign = '-';
        color = 'error.main';
      }

      return (
        <Typography variant='body2' color={color}>
          {`${sign} ${parseAmount(item.amount)}`}
        </Typography>
      );
    },
  },
  // TODO: UNCOMMENT WHEN BE FIX ISSUE WITH BALANCEAFTER DISPLAYING USER BALNACES INSTEAD OF CLIENT BALANCE {
  //   title: 'COMMON:TOTAL_BALANCE',
  //   content: (item) =>
  //     item.balanceAfter ? (
  //       <Typography variant='h5'>{parseAmount(item.balanceAfter)} </Typography>
  //     ) : (
  //       ''
  //     ),
  // },
];

export const DEFAULT_ROWS_PER_PAGE = 25;

export const validationSchema = Yup.object().shape({
  fromDate: VALIDATE_DATE.required(),
  toDate: VALIDATE_DATE.required(),
  transactionType: VALIDATE_STRING,
});

export const DATE_PICKER_PROPS = {
  size: 'medium',
  dateOnly: true,
  readOnly: false,
  disablePast: false,
  calendarViews: ['year', 'month', 'day'],
  fullWidth: true,
};

export const DATE_PICKERS = [
  { name: 'fromDate', label: 'COMMON:FROM_DATE' },
  { name: 'toDate', label: 'COMMON:TO_DATE' },
];

/**
 * Returns the fields of the movements search filters
 * @param {boolean} isArea if true, returns the filters for area movements
 */
export const FILTER_FIELDS = (isArea) => [
  {
    label: i18n.t('COMMON:TYPE'),
    getInitialValue: () => '',
    name: 'customerTransactionType',
    inputValidation: VALIDATE_STRING.required(),
    inputType: 'select',
    items: isArea ? AREA_TRANSACTION_FILTER_FIELDS : CLIENT_TRANSACTION_FILTER_FIELDS,
    parseOption: (type) => ({ label: i18n.t(type[1]), value: type[0] }),
  },
];
